import { useEffect, useRef, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { requestSubscription, graphql, type Environment } from 'react-relay/legacy.js';
import { type Disposable } from 'relay-runtime';
import { getBuyerId } from 'dibs-cookie-jar';

import {
    type nftItemTileUpdateSubscription,
    type NftItemUpdateSubscriptionInput,
} from './__generated__/nftItemTileUpdateSubscription.graphql.ts';

type nftItemType = NonNullable<
    NonNullable<nftItemTileUpdateSubscription['response']>['nftItemUpdate']
>['nftItem'];

export default function nftItemUpdate(
    environment: Environment,
    input: NftItemUpdateSubscriptionInput,
    onItemUpdate?: (nftItem: nftItemType) => void
): Disposable {
    const subscription = graphql`
        subscription nftItemTileUpdateSubscription(
            $input: NftItemUpdateSubscriptionInput!
            $userId: String!
            $fetchUser: Boolean!
        ) {
            nftItemUpdate(input: $input) {
                nftItem {
                    ...CollectorTileContent_nftItem
                }
            }
        }
    `;

    const userId = getBuyerId(document.cookie);

    return requestSubscription<nftItemTileUpdateSubscription>(environment, {
        subscription,
        variables: { input, userId, fetchUser: !!userId },
        onNext: payload => {
            if (typeof onItemUpdate === 'function') {
                onItemUpdate(payload?.nftItemUpdate?.nftItem || null);
            }
        },
    });
}

export const useNftItemTileUpdateSubscription = (
    itemId: string | null | undefined
): nftItemType => {
    const itemUpdateRef = useRef<Disposable | null>(null);
    const environment = useRelayEnvironment();
    const [nftItem, setNftItem] = useState<nftItemType>(null);

    useEffect(() => {
        if (!itemUpdateRef.current && itemId) {
            itemUpdateRef.current = nftItemUpdate(
                environment,
                {
                    itemId,
                },
                setNftItem
            );
        }
        return () => {
            if (itemUpdateRef.current) {
                itemUpdateRef.current.dispose();
            }
        };
    }, [environment, itemId]);

    return nftItem;
};
